import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LightTooltip, {
  ENTER_TOUCH_DELAY,
  LEAVE_TOUCH_DELAY,
} from "./LightTooltip";
import {
  PatientDetailData,
  RiskAssessmentType,
  useSetProjectSpecificFieldMutation,
} from "../features/data/patientApiSlice";
import React, { useCallback, useState } from "react";
import { createStyleSheet, globalStyles } from "../utils/styles";

import ClinicianBadgeGroup from "./ClinicianBadgeGroup";
import ClinicianBadgeList from "./ClinicianBadgeList";
import DetailCell from "./DetailCell";
import EditIcon from "@mui/icons-material/Edit";
import EditOutlineIcon from "@mui/icons-material/EditOutlined";
import IconButton from "./IconButton";
import LanguageIcon from "@mui/icons-material/Translate";
import MultiRiskAssessmentCell from "./MultiRiskAssessmentCell";
import ReminderIcon from "@mui/icons-material/NotificationsActive";
import RemindersList from "./RemindersList";
import RiskAssessmentDetail from "./RiskAssessmentDetail";
import SingleRiskAssessmentCell from "./SingleRiskAssessmentCell";
import SpeechBubble from "./SpeechBubble";
import { theme } from "../../theme";
import { useTranslation } from "react-i18next";

const MAX_BADGES = 8;

const PatientHeader = (props: { patient: PatientDetailData }) => {
  const { patient } = props;
  const { t } = useTranslation("Patient");

  // Component state
  const [remindersAnchor, setRemindersAnchor] = useState<null | HTMLElement>(
    null
  );
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [partnerId, setPartnerId] = useState(
    patient.project_specific_fields["partner_id"] || ""
  );

  // Queries/mutations
  const [setProjectSpecificField] = useSetProjectSpecificFieldMutation();

  // Callbacks
  const handleRemindersClick = (event: React.MouseEvent<HTMLElement>) => {
    setRemindersAnchor(event.currentTarget);
    setRemindersOpen((prev) => !prev);
  };
  const handleReminderClickAway = () => {
    setRemindersOpen(false);
  };
  const handleEditClick = () => {
    setEditMode(true);
  };
  const handleCancelClick = () => {
    setEditMode(false);
  };
  const handleSaveClick = useCallback(() => {
    if (partnerId !== patient.project_specific_fields["partner_id"]) {
      setProjectSpecificField({
        uid: patient.uid,
        fieldName: "partner_id",
        value: partnerId,
      });
    }
    setEditMode(false);
  }, [
    partnerId,
    patient.uid,
    patient.project_specific_fields,
    setProjectSpecificField,
  ]);

  // We're assuming there's only one risk assessment of either type
  const hivRiskAssessment = patient.risk_assessments?.find(
    (ra) =>
      ra.type === RiskAssessmentType.PhithosHIVRisk ||
      ra.type === RiskAssessmentType.CelosiaHIVRisk
  );
  const nonHivRiskAssessments = patient.risk_assessments?.filter(
    (ra) => ra.type !== hivRiskAssessment?.type
  );
  const partnerIdDescription =
    patient.project_specific_field_descriptions.partner_id ?? t("partnerId");

  return (
    <Box sx={styles.headerContainer}>
      <Box sx={styles.titleContainer}>
        <Typography variant="detailHeaderTitle">
          {patient.name || t("noName")}
        </Typography>
        {patient.pending_hcw_message ? <Box sx={styles.newMessageDot} /> : null}
        <Tooltip
          title={t("preferredLanguage", {
            language: t("common:language:" + patient.preferred_language),
          })}
          enterTouchDelay={ENTER_TOUCH_DELAY}
          leaveTouchDelay={LEAVE_TOUCH_DELAY}
          onContextMenu={(event) => event.preventDefault()}
        >
          <div>
            <SpeechBubble sx={styles.speechBubble}>
              <Box sx={styles.preferredLanguageContainer}>
                <LanguageIcon
                  color="primary"
                  sx={{ fontSize: "1rem", marginRight: 0.5 }}
                />
                <Typography variant="detailCellTitle">
                  {t("common:language:" + patient.preferred_language)}
                </Typography>
              </Box>
            </SpeechBubble>
          </div>
        </Tooltip>
        {patient.reminders && patient.reminders.length > 0 ? (
          <>
            <IconButton
              icon={<ReminderIcon color="primary" />}
              tooltip={t("reminders", { count: patient.reminders.length })}
              containerProps={{ sx: styles.remindersButton }}
              onClick={handleRemindersClick}
            />
            <RemindersList
              open={remindersOpen}
              anchorEl={remindersAnchor}
              onClickAway={handleReminderClickAway}
              patient={patient}
            />
          </>
        ) : null}
        <Box sx={globalStyles.flexSpace} />
        <Typography
          variant="detailCellTitle"
          sx={styles.workingInRecordText}
        >
          {t("workingInRecord")}
        </Typography>
        <LightTooltip
          title={
            <ClinicianBadgeList
              clinicians={patient.clinicians_working_in_record || []}
            />
          }
          arrow
        >
          <div>
            <ClinicianBadgeGroup
              clinicians={patient.clinicians_working_in_record || []}
              maxBadges={MAX_BADGES}
            />
          </div>
        </LightTooltip>
      </Box>
      <Box sx={styles.patientInfoContainer}>
        {hivRiskAssessment ? (
          <LightTooltip
            title={
              <RiskAssessmentDetail
                riskAssessments={[hivRiskAssessment]}
                selectedAssessmentType={hivRiskAssessment.type}
              />
            }
            arrow
            disableFocusListener
            leaveTouchDelay={Number.MAX_VALUE}
          >
            <div>
              <SingleRiskAssessmentCell
                riskAssessment={hivRiskAssessment}
                first={true}
              />
            </div>
          </LightTooltip>
        ) : null}
        {(nonHivRiskAssessments?.length || 0) > 0 ? (
          <LightTooltip
            title={
              <RiskAssessmentDetail
                riskAssessments={nonHivRiskAssessments!}
                selectedAssessmentType={nonHivRiskAssessments![0].type}
              />
            }
            arrow
            disableFocusListener
            leaveTouchDelay={Number.MAX_VALUE}
          >
            <div>
              <MultiRiskAssessmentCell
                riskAssessments={nonHivRiskAssessments}
                first={!hivRiskAssessment}
              />
            </div>
          </LightTooltip>
        ) : null}
        <DetailCell
          title={t("referralId")}
          first={!hivRiskAssessment && !nonHivRiskAssessments?.length}
          last={true}
        >
          <Stack direction="column">
            <Typography variant="detailCellBody">
              {patient.referral_id || t("common:unknown")}
            </Typography>
            <Typography variant="detailCellTitle">
              {partnerIdDescription}
            </Typography>
            {!editMode ? (
              <Typography variant="detailCellBody">
                {partnerId || t("common:notSet")}
              </Typography>
            ) : (
              <TextField
                aria-label={partnerIdDescription}
                placeholder={partnerIdDescription}
                slotProps={{
                  htmlInput: {
                    sx: styles.partnerIdHtmlInput,
                    maxLength: 32,
                  },
                  input: {
                    sx: styles.partnerIdInput,
                    endAdornment: (
                      <InputAdornment position="end">
                        <EditOutlineIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                variant="outlined"
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
              />
            )}
          </Stack>
        </DetailCell>
        <Box sx={globalStyles.flexSpace} />
        {!editMode ? (
          <IconButton
            icon={<EditIcon color="primary" />}
            tooltip={t("common:edit")}
            containerProps={{ sx: { alignContent: "flex-end" } }}
            onClick={handleEditClick}
          />
        ) : null}
      </Box>
      {editMode ? (
        <Stack direction="row" sx={styles.editButtonRow}>
          <Button
            variant="outlined"
            sx={globalStyles.smallGapRight}
            onClick={handleCancelClick}
          >
            {t("common:cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveClick} autoFocus>
            {t("common:save")}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
};

const styles = createStyleSheet({
  headerContainer: {
    borderRadius: 1,
    boxShadow: (theme) => theme.shadows[2],
    backgroundColor: "white",
    p: 3,
    [theme.breakpoints.down("desktop")]: {
      p: 2,
    },
  },
  titleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  newMessageDot: {
    backgroundColor: (theme) => theme.palette.newMessageDot.main,
    marginLeft: 1,
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
  speechBubble: {
    marginLeft: 2,
  },
  remindersButton: {
    marginLeft: 1,
  },
  partnerIdHtmlInput: {
    p: 0,
  },
  editButtonRow: {
    ...globalStyles.gapTop,
    justifyContent: "flex-end",
  },
  partnerIdInput: {
    height: "23px",
    px: 1,
    py: 0,
    fontWeight: 500,
    fontSize: 14,
    [theme.breakpoints.down("desktop")]: {
      fontSize: 13,
    },
  },
  preferredLanguageContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: (theme) => theme.spacing(0.25, 0.5),
  },
  workingInRecordText: {
    marginRight: 1,
    textAlign: "right",
  },
  patientInfoContainer: {
    display: "flex",
    flexDirection: "row",
    marginTop: 0.5,
  },
});

export default PatientHeader;
